import { useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { buyersApi, type BuyerLeadDetailDto } from "@/api/buyers";
import { extractErrorMessage } from "@/api/client";
import { Button, Input, Textarea, useToast } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { Select } from "@/components/ui/Select";
import { buyerKeys } from "@/hooks/buyers/buyerKeys";
import {
  buildSectionPatch,
  hydrateSection,
  sectionIsDirty,
  visibleEditableFields,
  type BuyerFieldConfig,
  type BuyerSectionConfig,
  type BuyerSectionForm,
} from "./buyerSectionConfig";

/**
 * Edit ONE section of a buyer.
 *
 * <p>🔴 <b>Per-section, not per-record.</b> The whole-record edit page exists and
 * is still reachable from the header's overflow, but it is the wrong instrument
 * for the ordinary case: a specialist who has just been given a phone number
 * should not be handed forty fields, seven of which they will read past and one
 * of which they may fat-finger. A section modal sends only that section's keys,
 * so every field outside it is left literally untouched on the wire.</p>
 *
 * <p><b>Built on the shared <c>Modal</c>, which manages its own focus.</b> PR
 * #1043 gave it <c>useFocusTrap</c>, a <c>useId</c>-scoped title and
 * <c>initialFocusRef</c>. Two nested traps do not compose — both handle Tab, the
 * inner one calls <c>preventDefault</c> without <c>stopPropagation</c>, and focus
 * jumps twice at the cycle boundary — so this passes <c>initialFocusRef</c> and
 * adds nothing of its own.</p>
 *
 * <p><b>The backdrop does not dismiss it.</b> A half-typed form is exactly the
 * state a stray click lands in, and the <c>SectionEditDrawer</c> precedent makes
 * the same call.</p>
 */
export function BuyerSectionEditModal({
  section,
  buyer,
  open,
  onClose,
}: {
  section: BuyerSectionConfig;
  buyer: BuyerLeadDetailDto;
  open: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const firstFieldRef = useRef<HTMLElement | null>(null);

  const [form, setForm] = useState<BuyerSectionForm>(() => hydrateSection(section, buyer));
  const [error, setError] = useState<string | null>(null);

  // Rehydrate each time it opens, so a cancelled edit is never re-offered as if
  // it were the record's current state — the BuyerStatusControl convention.
  useEffect(() => {
    if (!open) return;
    setForm(hydrateSection(section, buyer));
    setError(null);
  }, [open, section, buyer]);

  const save = useMutation({
    mutationFn: () => buyersApi.update(buyer.id, buildSectionPatch(section, form, buyer)),
    onSuccess: () => {
      // The whole prefix: this record is read by the detail query, the strip's
      // submission roll-up and every list that renders the buyer's name, and the
      // shared QueryClient sits at a five-minute staleTime with no refetch on
      // focus — a missed invalidation is stale for minutes with no recovery.
      void qc.invalidateQueries({ queryKey: buyerKeys.all });
      toast.success(`${section.title} updated.`);
      onClose();
    },
    onError: (e: unknown) => setError(extractErrorMessage(e)),
  });

  const dirty = sectionIsDirty(section, buyer, form);

  return (
    <Modal
      open={open}
      onClose={() => (save.isPending ? undefined : onClose())}
      title={`Edit ${section.title.toLowerCase()}`}
      description={section.editDescription}
      size="md"
      dismissOnBackdropClick={false}
      initialFocusRef={firstFieldRef}
      footer={
        <div className="flex justify-end gap-space-2">
          <Button variant="text" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              setError(null);
              save.mutate();
            }}
            loading={save.isPending}
            disabled={save.isPending || !dirty}
            data-testid={`buyer-section-save-${section.id}`}
          >
            {save.isPending ? "Saving…" : "Save"}
          </Button>
        </div>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-space-3">
        {/* `visibleEditableFields`, not `section.fields`: a `readOnly` field is
            shown on the card and never asked for here, and a field whose
            `visibleWhen` fails (a company row for an individual) is not asked
            for either. The patch and the dirty check read the same subset, so
            neither kind can reach the wire. */}
        {visibleEditableFields(section, buyer, form).map((field, index) => (
          <FieldInput
            key={field.key}
            field={field}
            value={form[field.key] ?? ""}
            onChange={(next) => setForm((f) => ({ ...f, [field.key]: next }))}
            inputRef={index === 0 ? firstFieldRef : undefined}
            disabled={save.isPending}
          />
        ))}
        {error && (
          <p className="md:col-span-2 text-small text-status-error m-0" role="alert">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}

function FieldInput({
  field,
  value,
  onChange,
  inputRef,
  disabled,
}: {
  field: BuyerFieldConfig;
  value: string;
  onChange: (next: string) => void;
  inputRef?: React.MutableRefObject<HTMLElement | null>;
  disabled: boolean;
}) {
  const id = `buyer-field-${field.key}`;
  const wide = field.wide || field.type === "textarea";

  return (
    <label className={wide ? "block md:col-span-2" : "block"} htmlFor={id}>
      <span className="text-caption uppercase tracking-wide text-gray-mid">{field.label}</span>
      <div className="mt-space-1">
        {field.type === "textarea" ? (
          <Textarea
            id={id}
            rows={3}
            value={value}
            maxLength={field.maxLength}
            placeholder={field.hint}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value)}
            ref={inputRef as React.Ref<HTMLTextAreaElement>}
            data-testid={id}
          />
        ) : field.type === "yesno" ? (
          <Select
            id={id}
            value={value}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value)}
            ref={inputRef as React.Ref<HTMLSelectElement>}
            data-testid={id}
          >
            {/*
              🔴 Three options, not two. "Not stated" is the value that clears the
              field, and it is offered FIRST so the tri-state reads as a real
              answer rather than as a failure to choose one.
            */}
            <option value="">Not stated</option>
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </Select>
        ) : field.type === "select" ? (
          <Select
            id={id}
            value={value}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value)}
            ref={inputRef as React.Ref<HTMLSelectElement>}
            data-testid={id}
          >
            {/* "Not stated" first, as on the yes/no select: it is the value that
                clears the field through its flag. */}
            <option value="">Not stated</option>
            {(field.options ?? []).map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
        ) : (
          <Input
            id={id}
            type={inputType(field)}
            inputMode={field.type === "money" || field.type === "int" ? "decimal" : undefined}
            value={value}
            maxLength={field.maxLength}
            placeholder={field.hint}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value)}
            ref={inputRef as React.Ref<HTMLInputElement>}
            data-testid={id}
          />
        )}
      </div>
      {(field.type === "money" || field.type === "int" || field.clearFlag) && (
        <span className="text-caption text-gray-mid">
          Leave blank to remove.
        </span>
      )}
    </label>
  );
}

function inputType(field: BuyerFieldConfig): string {
  switch (field.type) {
    case "email":
      return "email";
    case "tel":
      return "tel";
    case "date":
      return "date";
    // 🔴 Money and integers are TEXT inputs, not `type="number"`. A number input
    // silently discards a value the browser considers malformed on blur, and it
    // reports "" for both "the user cleared it" and "the user typed something I
    // did not like" — which is precisely the distinction the clear flags exist to
    // carry. `inputMode="decimal"` still gets the numeric keypad on touch.
    default:
      return "text";
  }
}
